/**
 * 🐑🥣 **FODDER — the jata, sheep hunger, and the winter (T286).**
 *
 * 🎯 **haugur, 2026-10-01**, every number below chosen or confirmed by him:
 *
 * | | |
 * |---|---|
 * | 🎯 *"1 sheep need 1 unit of grass a day"* | **one grass block grazed, or one úthey, = one sheep fed one game day** — the {@link SHEEP_DAY} |
 * | 🎯 *"1 dies"* | **an unfed sheep DIES** — full to dead in {@link FULL_DAYS} game days, a real ewe under snow |
 * | 🎯 *"like a slot, you can put alot of units into it"* | **the jata holds {@link JATA_CAPACITY} sheep-days** |
 * | 🎯 the table, *"ye"* | **taða is worth 2, úthey 1** · no regrowth November–April |
 *
 * 📖 **Why it is shaped like this — the research of 2026-10-01.** Icelandic
 * farming's limit for a thousand years was **how much hay you put up in the
 * summer**; *horfellir*, stock dying for lack of it, was the great fear (1783–84
 * lost about 80% of the country's sheep). **Minecraft has no animal hunger at
 * all.** ⚠️ **The mod that has it, Animania, is famous for animals starving
 * BESIDE a full trough** because they could not path to it — which is why a jata
 * feeds its whole PEN and no sheep has to reach it.
 *
 * 🔴 **"Grass first" is not a rule anywhere in here, and that is on purpose.** A
 * grazing sheep tops itself up a bite at a time and stays near full; only a sheep
 * that is NOT getting enough grass ever falls far enough to take from the
 * trough. **The order emerges** — no pen has to be scanned for grass.
 *
 * Pure: no server, no clock reads. Everything takes the numbers it needs.
 */

/** 🐑 One sheep fed for one game day — the unit everything here counts in. */
export const SHEEP_DAY = 1;

/** 🌾 What one hay is worth, in sheep-days. History: taða was the better hay. */
export const FODDER_VALUE: ReadonlyMap<string, number> = new Map([
  ['tada', 2],
  ['uthey', 1],
]);

/** 🥣 How much a jata holds, in sheep-days — haugur's 300. */
export const JATA_CAPACITY = 300;

/**
 * 🥣 **How many steps the hay rises in** — 🎯 *"smooth hay"*. A block state per
 * step; 30 steps is 10 sheep-days a step, which reads as smooth at any distance
 * a player stands from a manger.
 */
export const JATA_FILL_STEPS = 30;

/** 🐑 **Full to dead, in game days** — haugur's 18 (about six hours of play). */
export const FULL_DAYS = 18;

/** Below this the sheep is HUNGRY: no wool regrowth, cannot be made ready. */
export const HUNGRY_BELOW = 12;

/** Below this the sheep is STARVING — the warnings fire. */
export const STARVING_BELOW = 6;

/**
 * 🌿 **What one graze is worth, in sheep-days, on grass it can eat.**
 *
 * 📏 A sheep grazes about every 26 s for 8 s (`sheepmind.ts`), about 46 times a
 * game day. At 0.1 a bite a sheep CAN eat about 4.6 a day — so it recovers from
 * hunger in a few days on good grass — but it only ever eats what it has room
 * for, so a full sheep eats exactly what it burns: **one a day, one block a day.**
 */
export const GRAZE_BITE = 0.1;

/**
 * 🌿 The blocks that feed a grazing sheep — the same ones a ljár cuts (D131).
 *
 * 🔴 **Birch scrub is NOT one of them, on purpose.** 🎯 haugur, 2026-10-02,
 * offered birki at half value: *"let them eat from jata."* A sheep still
 * GRAZES there (`grazingScore` scores birki 6) — it just gets nothing from it.
 */
export const EDIBLE_GRASS: ReadonlySet<string> = new Set(['tun', 'gras', 'myri']);

export type HungerStage = 'fed' | 'hungry' | 'starving';

/** How a sheep is doing, from its reserve. */
export function hungerStage(reserve: number): HungerStage {
  if (reserve < STARVING_BELOW) return 'starving';
  if (reserve < HUNGRY_BELOW) return 'hungry';
  return 'fed';
}

/** A reserve kept inside 0 … {@link FULL_DAYS}, with a broken value read as full. */
export function clampReserve(reserve: unknown): number {
  if (typeof reserve !== 'number' || !Number.isFinite(reserve)) return FULL_DAYS;
  return Math.min(FULL_DAYS, Math.max(0, reserve));
}

/** 🐑 A reserve after `days` of game time with nothing eaten. */
export function burn(reserve: number, days: number): number {
  return clampReserve(reserve - Math.max(0, days) * SHEEP_DAY);
}

/** 💀 Has this sheep starved? */
export function hasStarved(reserve: number): boolean {
  return reserve <= 0;
}

/**
 * 🌿 **One graze.** Eats what the sheep has room for, up to a bite.
 *
 * @returns the new reserve and how much was eaten — the caller adds `eaten` to
 *   the sheep's grass tally, and every whole sheep-day of it turns one block to
 *   stubble.
 */
export function eatGrass(reserve: number): { reserve: number; eaten: number } {
  const eaten = Math.max(0, Math.min(GRAZE_BITE, FULL_DAYS - reserve));
  return { reserve: clampReserve(reserve + eaten), eaten };
}

/**
 * 🥣 **Does this sheep go to the trough?** Only once it has room for a whole
 * sheep-day — so a sheep on grass never does, because grazing keeps it within a
 * bite of full.
 */
export function wantsTrough(reserve: number): boolean {
  return reserve <= FULL_DAYS - SHEEP_DAY;
}

/** 🥣 What a jata holds: how many of each hay, so breaking it gives them back. */
export interface JataStore {
  readonly tada: number;
  readonly uthey: number;
}

export const EMPTY_JATA: JataStore = { tada: 0, uthey: 0 };

/** 🥣 A jata's fodder in sheep-days. */
export function jataDays(store: JataStore): number {
  return (
    store.tada * (FODDER_VALUE.get('tada') ?? 2) + store.uthey * (FODDER_VALUE.get('uthey') ?? 1)
  );
}

/** 🥣 A stored row, read without trusting it. */
export function readJata(raw: unknown): JataStore {
  if (typeof raw !== 'object' || raw === null) return EMPTY_JATA;
  const row = raw as Record<string, unknown>;
  const n = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : 0);
  const store = { tada: n(row['tada']), uthey: n(row['uthey']) };
  /* ⚠️ A row claiming more than a jata holds is clamped, poorer hay first. */
  let over = jataDays(store) - JATA_CAPACITY;
  if (over <= 0) return store;
  const uthey = Math.max(0, store.uthey - over);
  over -= store.uthey - uthey;
  const tada = Math.max(0, store.tada - Math.ceil(Math.max(0, over) / 2));
  return { tada, uthey };
}

/**
 * 🥣 **Pour hay into a jata.** Whole hay only: a taða needs room for two.
 *
 * @returns the new store and how many were USED — the caller takes exactly that
 *   many from the player's hand. ⚠️ Anything that is not hay uses none.
 */
export function fillJata(
  store: JataStore,
  itemKey: string,
  count: number,
): { store: JataStore; used: number } {
  const value = FODDER_VALUE.get(itemKey);
  if (value === undefined || !Number.isInteger(count) || count <= 0) return { store, used: 0 };
  const room = JATA_CAPACITY - jataDays(store);
  const used = Math.max(0, Math.min(count, Math.floor(room / value)));
  if (used === 0) return { store, used: 0 };
  return {
    store:
      itemKey === 'tada'
        ? { ...store, tada: store.tada + used }
        : { ...store, uthey: store.uthey + used },
    used,
  };
}

/**
 * 🥣 **A sheep eats one sheep-day from the jata.** Úthey first — the poorer hay
 * went to the sheep (history), and the taða is what a farmer keeps back.
 *
 * ⚠️ A taða is two sheep-days, so eating one from a jata with only taða in it
 * takes a whole taða and gives the sheep two — never half a hay.
 *
 * @returns `null` when the jata is empty.
 */
export function eatFromJata(store: JataStore): { store: JataStore; days: number } | null {
  if (store.uthey > 0) return { store: { ...store, uthey: store.uthey - 1 }, days: 1 };
  if (store.tada > 0) return { store: { ...store, tada: store.tada - 1 }, days: 2 };
  return null;
}

/** 🥣 The fill step a jata shows, 0 … {@link JATA_FILL_STEPS}. Any hay at all shows. */
export function jataFillStep(store: JataStore): number {
  const days = jataDays(store);
  if (days <= 0) return 0;
  return Math.min(
    JATA_FILL_STEPS,
    Math.max(1, Math.round((days / JATA_CAPACITY) * JATA_FILL_STEPS)),
  );
}

/* ── The winter: no regrowth November to April ─────────────────────────── */

/** 📅 Day of the year 1 May (121) and 31 October (304), in a 365-day year. */
const GROWING_FIRST = 121;
const GROWING_LAST = 304;
const GROWING_DAYS = GROWING_LAST - GROWING_FIRST + 1;

/**
 * 🌱 **How many GROWING days have passed by world-day `t`** (fractional days
 * since the calendar's 1 January of year nought). Closed form, so asking it is
 * as cheap for a stubble cut a year ago as one cut this morning.
 */
function growingDaysBy(t: number): number {
  const years = Math.floor(t / 365);
  const into = t - years * 365;
  return years * GROWING_DAYS + Math.min(GROWING_DAYS, Math.max(0, into - (GROWING_FIRST - 1)));
}

/**
 * 🌱 **Growing days between two moments**, given as played milliseconds.
 *
 * 🎯 haugur's *"Yes, November to April"*: grass does not grow back in the
 * winter, so the flock lives on what was put up in the summer. **Only May to
 * October counts toward a stubble's ten days.**
 *
 * @param startDayOfYear the calendar day the world began on (25 May).
 * @param startFraction how far into that day the world began.
 * @param dayMs real milliseconds in a game day.
 */
export function growingDaysBetween(
  fromPlayedMs: number,
  toPlayedMs: number,
  startDayOfYear: number,
  startFraction: number,
  dayMs: number,
): number {
  const at = (ms: number): number => startDayOfYear - 1 + startFraction + ms / dayMs;
  return Math.max(0, growingDaysBy(at(toPlayedMs)) - growingDaysBy(at(fromPlayedMs)));
}
